import { z } from 'zod';

/**
 * `MsgType` values assigned by the Ava org API when it builds a normalized-log response. These are
 * the semantic message names, so they are also what the wire-log tool filters on.
 */
export const OrderLogMsgType = {
  OMSDecisionLog: 'OMS Decision Log',
  OMSExecutionReport: 'OMS Execution Report',
  MarketExecutionReport: 'Market Execution Report',
  MarketTrade: 'Market Trade',
  SystemNewOrderSingle: 'System New Order Single',
  SystemCancelRequest: 'System Cancel Request',
  SystemCancelReplaceRequest: 'System Cancel Replace Request',
  OrderCancelReject: 'Order Cancel Reject',
  GatewayOrderUpdate: 'Gateway Order Update',
  GatewayOrderSending: 'Gateway Order Sending',
  GatewayCancelRequest: 'Gateway Cancel Request',
  GatewayReplaceRequest: 'Gateway Replace Request',
  GatewayRecovery: 'Gateway Recovery',
  ContinuousRecovery: 'Continuous Recovery',
} as const;
export type OrderLogMsgType = (typeof OrderLogMsgType)[keyof typeof OrderLogMsgType];

/** Message types that `getOrderWireLogs` and `getChildOrderLogs` can filter on. */
const wireLogMsgTypes = [
  OrderLogMsgType.MarketExecutionReport,
  OrderLogMsgType.MarketTrade,
  OrderLogMsgType.SystemNewOrderSingle,
  OrderLogMsgType.SystemCancelRequest,
  OrderLogMsgType.SystemCancelReplaceRequest,
  OrderLogMsgType.OrderCancelReject,
  OrderLogMsgType.GatewayOrderUpdate,
  OrderLogMsgType.GatewayOrderSending,
  OrderLogMsgType.GatewayCancelRequest,
  OrderLogMsgType.GatewayReplaceRequest,
  OrderLogMsgType.GatewayRecovery,
  OrderLogMsgType.ContinuousRecovery,
] as const;

export const wireLogMsgTypeSchema = z
  .enum(wireLogMsgTypes)
  .optional()
  .describe(
    'Return only this message type. Strongly preferred: unfiltered wire logs are large and mostly irrelevant. "Gateway Order Update" carries inbound venue payloads with exchange-side timestamps.'
  );

export const orderIdSchema = z.string().min(1).describe('Talos parent OrderID, as shown in the Ava UI order details.');

/**
 * Every order-log tool returns this shape. `found: false` is not the same as "the order did
 * nothing": see `note`, which explains when records may never have been written at all.
 */
export const orderLogsResultSchema = z.object({
  orderId: z.string(),
  found: z.boolean(),
  /** Shaped, size-bounded text for the model to read. Empty when `found` is false. */
  content: z.string(),
  /** Record count per `MsgType`, so the model knows what it has before reading `content`. */
  recordCounts: z.record(z.string(), z.number()).optional(),
  /** Set when `content` was cut to stay within the response budget. */
  truncated: z.boolean().optional(),
  /** Explains an empty result or a likely wrong ID, or warns that the fetch hit its record cap. */
  note: z.string().optional(),
});
export type OrderLogsResult = z.infer<typeof orderLogsResultSchema>;
